import { useCallback, useEffect, useState } from 'react';

import { DateTime } from 'luxon';

export type ApiResponse<T> = {
    data: T;
    hasLoaded: boolean;
    isLoading: boolean;
    isInitialLoad: boolean;
    responseUrl?: string;
    isError: boolean;
    responseCode?: number;
};

export type ApiResponseNHL<T> = ApiResponse<T> & {
    resolvedDate?: DateTime;
    resolvedSeason?: number;
    refetch: () => void;
};

export enum ApiResponseCode {
    OK = 200,
    NOT_FOUND = 404,
}

const DATE_PATTERN = /[0-9]{4}-[0-9]{2}-[0-9]{2}/;
const YEAR_MONTH_PATTERN = /[0-9]{4}-[0-9]{2}/;
const SEASON_PATTERN = /[0-9]{8}/;

export const useFetchLeagueData = <T>(endpoint: string, isEnabled = true, baseUrl?: string): ApiResponseNHL<T> => {
    const [data, setData] = useState<T>({} as T);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(isEnabled);
    const [isError, setIsError] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [responseCode, setResponseCode] = useState<ApiResponseCode>();
    const [responseUrl, setResponseUrl] = useState<string>();
    const [resolvedDate, setResolvedDate] = useState<DateTime>();
    const [resolvedSeason, setResolvedSeason] = useState<number>();
    const [refetchJiggler, setRefetchJiggler] = useState<number>();

    const fetchData = useCallback(
        async (updateLoadingStatus: boolean) => {
            updateLoadingStatus && setHasLoaded(false);
            updateLoadingStatus && setIsLoading(true);
            const response = await fetch((baseUrl || window.__SITE_SETTINGS__.webAPIBaseURL) + endpoint, {
                credentials: 'include',
            });

            setResponseCode(response.status);

            if (response.ok) {
                const dateString = response.url.match(DATE_PATTERN)?.[0] || response.url.match(YEAR_MONTH_PATTERN)?.[0];

                if (dateString !== undefined) {
                    const date = DateTime.fromISO(dateString);

                    if (date.isValid) {
                        setResolvedDate(date);
                    }
                }

                const seasonString = response.url.match(SEASON_PATTERN)?.[0];

                if (seasonString !== undefined) {
                    setResolvedSeason(Number(seasonString));
                }

                setResponseUrl(response.url);

                const responseJson = await response.json();
                setData(responseJson);
            } else {
                setIsError(true);

                const message = `An error has occurred: ${response.status}`;
                throw new Error(message);
            }

            updateLoadingStatus && setHasLoaded(true);
            updateLoadingStatus && setIsLoading(false);
            updateLoadingStatus && setIsInitialLoad(false);
        },
        [baseUrl, endpoint]
    );

    useEffect(() => {
        if (isEnabled) {
            fetchData(true);
        }
    }, [fetchData, isEnabled]);

    useEffect(() => {
        if (isEnabled && refetchJiggler !== undefined) {
            fetchData(false);
        }
    }, [fetchData, isEnabled, refetchJiggler]);

    // basically what we're doing here is giving any user of useFetchLeagueData a way to force a refetch of data.
    // to accomplish this we just increment the "jiggler". By altering this value we trigger the useEffect above
    // which will cause a fetch that will ALSO skip any loading indicator updating. Note that the effect above will
    // NOT trigger if the jiggler is undefined, ie was never initialized. In this situation, refetch has never
    // been called and, so, refetching should naturally never happen. Of course, if the hook is disabled this is still
    // honored.
    const refetch = () => {
        setRefetchJiggler((refetchJiggler || 0) + 1);
    };

    return {
        data,
        hasLoaded,
        isLoading,
        isInitialLoad,
        isError,
        responseCode,
        responseUrl,
        resolvedDate,
        resolvedSeason,
        refetch,
    };
};

export const useFetchData = <T>(endpoint: string, isEnabled = true): ApiResponse<T> => {
    const [data, setData] = useState<T>({} as T);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(isEnabled);
    const [isError, setIsError] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [responseCode, setResponseCode] = useState<ApiResponseCode>();
    const [responseUrl, setResponseUrl] = useState<string>();

    const fetchData = useCallback(async () => {
        setHasLoaded(false);
        setIsLoading(true);
        const response = await fetch(endpoint);

        setResponseCode(response.status);

        if (response.ok) {
            setResponseUrl(response.url);

            const responseJson = await response.json();
            setData(responseJson);
        } else {
            setIsError(true);

            const message = `An error has occurred: ${response.status}`;
            throw new Error(message);
        }

        setHasLoaded(true);
        setIsLoading(false);
        setIsInitialLoad(false);
    }, [endpoint]);

    useEffect(() => {
        if (isEnabled) {
            fetchData();
        }
    }, [fetchData, isEnabled]);

    return {
        data,
        hasLoaded,
        isLoading,
        isInitialLoad,
        isError,
        responseCode,
        responseUrl,
    };
};
